import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowRight, Shield, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import AuthFooter from '../components/AuthFooter';

type Phase = 'verifying' | 'choose-password' | 'invalid';

const MIN_PASSWORD_LENGTH = 8;

export default function SetPassword() {
  const { verifyEmailLinkToken, setPassword: doSetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const tokenHash = searchParams.get('token_hash');
  const linkType = (searchParams.get('type') ?? 'recovery') as
    | 'recovery'
    | 'invite'
    | 'magiclink'
    | 'signup'
    | 'email_change';
  const email = searchParams.get('email') ?? '';

  const [phase, setPhase] = useState<Phase>(tokenHash ? 'verifying' : 'invalid');
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tokenHash) {
      setError('This link is missing or malformed. Please check your email or request a new one.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await verifyEmailLinkToken(tokenHash, linkType);
        if (!cancelled) {
          setPhase('choose-password');
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setPhase('invalid');
        setError(humaniseError(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tokenHash, linkType, verifyEmailLinkToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords don\u2019t match.');
      return;
    }

    setLoading(true);
    try {
      await doSetPassword(password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(humaniseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B2341] via-[#1A3A5C] to-[#0B2341] grid place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center gap-3 mb-4">
            <img
              src="https://adiimmigration.com.au/wp-content/uploads/2021/06/ADI_Immigration-white.png"
              alt="ADI Immigration"
              className="w-24 h-24"
            />
            <div className="text-slate-300 text-sm">Client Portal</div>
          </div>
          <h1 className="text-white text-2xl font-semibold mb-1">Set your password</h1>
          <p className="text-slate-300 text-sm">
            {email ? `Choose a password for ${email}.` : 'Choose a password to access your portal.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {phase === 'verifying' && (
            <div className="text-center py-8 text-slate-500 text-sm">Verifying your link...</div>
          )}

          {phase === 'invalid' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error ?? 'This link is invalid or has expired.'}
              </div>
              <p className="text-sm text-slate-600">
                Links expire after 24 hours and can only be used once. Head to the sign-in page to
                request a fresh one.
              </p>
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                className="btn-primary w-full justify-center"
              >
                Go to sign in <ArrowRight size={16} />
              </button>
            </div>
          )}

          {phase === 'choose-password' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="form-label">New password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPasswordValue(e.target.value)}
                    placeholder="At least 8 characters"
                    className="form-input pl-10 pr-10"
                    required
                    autoFocus
                    autoComplete="new-password"
                    minLength={MIN_PASSWORD_LENGTH}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={show ? 'Hide password' : 'Show password'}
                  >
                    {show ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="form-label">Confirm password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={show ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter password"
                    className="form-input pl-10"
                    required
                    autoComplete="new-password"
                    minLength={MIN_PASSWORD_LENGTH}
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
                {loading ? 'Saving...' : <>Save password &amp; sign in <ArrowRight size={16} /></>}
              </button>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
              <Shield size={12} />
              Secured sign-in. We never share your data.
            </p>
          </div>
        </div>

        <div className="mt-6 text-center text-slate-400 text-xs">
          <p>
            Need help? Call{' '}
            <a href="tel:1300729331" className="text-[#2CABEA] hover:underline">
              1300 729 331
            </a>{' '}
            or email{' '}
            <a href="mailto:adi@adibusinessgroup.com" className="text-[#2CABEA] hover:underline">
              adi@adibusinessgroup.com
            </a>
          </p>
        </div>

        <AuthFooter />
      </div>
    </div>
  );
}

function humaniseError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? 'Something went wrong.');
  const lower = raw.toLowerCase();
  if (lower.includes('expired') || lower.includes('invalid') && lower.includes('token')) {
    return 'This link has expired or already been used. Request a fresh one from the sign-in page.';
  }
  if (lower.includes('rate') || lower.includes('too many')) {
    return 'Too many attempts. Please wait a minute and try again.';
  }
  if (lower.includes('weak') || lower.includes('password')) {
    return raw;
  }
  return raw;
}
